import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Users, UserPlus, Shield, Mail, MoreVertical, CheckCircle, XCircle,
  RefreshCw, Copy, ArrowLeft
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_CONFIG = {
  owner: { label: 'Owner', color: '#9F7AEA', bg: '#9F7AEA20' },
  manager: { label: 'Manager', color: '#3182CE', bg: '#3182CE20' },
  staff: { label: 'Staff', color: '#38A169', bg: '#38A16920' }
};

export default function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'staff' });
  const [inviting, setInviting] = useState(false);
  const token = localStorage.getItem('auth_token');
  const currentUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { if (token) fetchUsers(); else setLoading(false); }, [token]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers });
      setUsers(res.data.items);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  const inviteUser = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) { toast.error('Email required'); return; }
    setInviting(true);
    try {
      const res = await axios.post(`${API}/users/invite`, inviteForm, { headers });
      toast.success(`Invitation sent to ${inviteForm.email}`);
      if (res.data.invite_token) {
        const link = `${window.location.origin}/accept-invite?token=${res.data.invite_token}`;
        navigator.clipboard?.writeText(link);
        toast.success('Invite link copied to clipboard');
      }
      setShowInvite(false);
      setInviteForm({ email: '', name: '', role: 'staff' });
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to send invite'); }
    finally { setInviting(false); }
  };

  const updateRole = async (userId, role) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role }, { headers });
      toast.success(`Role updated to ${role}`);
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update role'); }
  };

  const toggleStatus = async (userId, isActive) => {
    try {
      await axios.put(`${API}/users/${userId}/status`, { is_active: isActive }, { headers });
      toast.success(`User ${isActive ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update status'); }
  };

  const resendInvite = async (userId) => {
    try {
      const res = await axios.post(`${API}/users/${userId}/resend-invite`, {}, { headers });
      if (res.data.invite_token) {
        const link = `${window.location.origin}/accept-invite?token=${res.data.invite_token}`;
        navigator.clipboard?.writeText(link);
        toast.success('New invite link copied to clipboard');
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to resend invite'); }
  };

  if (!token) {
    return <div className="settings-page"><div className="billing-empty"><h2>Please log in</h2><button onClick={() => navigate('/login')} className="checkout-btn">Go to Login</button></div></div>;
  }

  return (
    <div className="settings-page" data-testid="user-management">
      <div className="settings-header">
        <button className="back-btn" onClick={() => navigate('/dashboard')}><ArrowLeft size={18} /> Dashboard</button>
        <div className="settings-title-row">
          <div>
            <h1>Team Members</h1>
            <p>{users.length} users</p>
          </div>
          {['owner', 'manager'].includes(currentUser.role) && (
            <button className="btn-primary" onClick={() => setShowInvite(true)} data-testid="invite-user-btn">
              <UserPlus size={16} /> Invite User
            </button>
          )}
        </div>
      </div>

      <div className="saas-table-container">
        <table className="saas-table" data-testid="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.staff;
              return (
                <tr key={u.id}>
                  <td>
                    <div className="tenant-cell">
                      <div className="tenant-avatar" style={{ background: rc.bg, color: rc.color }}>{u.name?.charAt(0) || 'U'}</div>
                      <div className="tenant-info">
                        <strong>{u.name}</strong>
                        <span>{u.email}</span>
                      </div>
                    </div>
                  </td>
                  <td><span className="priority-badge" style={{ background: rc.bg, color: rc.color }}>{rc.label}</span></td>
                  <td>
                    {u.is_active ? (
                      <span className="status-badge active">Active</span>
                    ) : (
                      <span className="status-badge canceled">Inactive</span>
                    )}
                    {u.invite_accepted === false && <span className="status-badge paused" style={{ marginLeft: 4 }}>Pending</span>}
                  </td>
                  <td className="timestamp-cell">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="timestamp-cell">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never'}</td>
                  <td>
                    <div className="action-buttons">
                      {currentUser.role === 'owner' && u.id !== currentUser.id && (
                        <div className="action-dropdown">
                          <button className="action-btn"><Shield size={16} /></button>
                          <div className="dropdown-menu">
                            {['owner', 'manager', 'staff'].filter(r => r !== u.role).map(r => (
                              <button key={r} onClick={() => updateRole(u.id, r)}>Set {ROLE_CONFIG[r].label}</button>
                            ))}
                          </div>
                        </div>
                      )}
                      {u.invite_accepted === false && (
                        <button className="action-btn" title="Resend Invite" onClick={() => resendInvite(u.id)} data-testid={`resend-${u.id}`}>
                          <RefreshCw size={16} />
                        </button>
                      )}
                      {u.id !== currentUser.id && ['owner', 'manager'].includes(currentUser.role) && (
                        <button className="action-btn" title={u.is_active ? 'Deactivate' : 'Activate'}
                          onClick={() => toggleStatus(u.id, !u.is_active)} data-testid={`toggle-${u.id}`}>
                          {u.is_active ? <XCircle size={16} /> : <CheckCircle size={16} />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showInvite && (
        <div className="slide-over-overlay" onClick={() => setShowInvite(false)}>
          <div className="slide-over" onClick={e => e.stopPropagation()} data-testid="invite-modal">
            <div className="slide-over-header">
              <h3>Invite Team Member</h3>
              <button className="close-btn" onClick={() => setShowInvite(false)}><XCircle size={20} /></button>
            </div>
            <div className="slide-over-content">
              <form onSubmit={inviteUser} className="settings-form" style={{ maxWidth: '100%' }}>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Email *</label>
                  <input className="form-input" type="email" value={inviteForm.email}
                    onChange={(e) => setInviteForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="user@company.com" data-testid="invite-email" />
                </div>
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input className="form-input" value={inviteForm.name}
                    onChange={(e) => setInviteForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Full name" data-testid="invite-name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-input" value={inviteForm.role}
                    onChange={(e) => setInviteForm(p => ({ ...p, role: e.target.value }))} data-testid="invite-role">
                    <option value="staff">Staff</option>
                    <option value="manager">Manager</option>
                    {currentUser.role === 'owner' && <option value="owner">Owner</option>}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <button type="submit" className="btn-primary" disabled={inviting} style={{ width: '100%', justifyContent: 'center' }} data-testid="send-invite-btn">
                    {inviting ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
